<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { h, ref, computed, onMounted, watch, defineComponent, createCommentVNode, type PropType } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import type {
  ICartPaymentMethodGetListItemParameters,
  IAoeCart,
  IAoeCartPayment,
} from '../../types/CartTypes'
import type { AoeMessageEvent, IErrorResponse } from '../../types/AoeTypes'

import {
  ExpansionPanel as KExpansionPanel,
  ExpansionPanelContent as KExpansionPanelContent,
} from '@progress/kendo-vue-layout'
import { Reveal as KReveal } from '@progress/kendo-vue-animation'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Loader as KLoader } from '@progress/kendo-vue-indicators'
import { trashIcon } from '@progress/kendo-svg-icons'
import { Grid } from '@progress/kendo-vue-grid'

import BrainTree from '../PaymentGateways/Braintree/BraintreePaymentEntry.vue'
import BraintreeApplePayPaymentEntry from '../PaymentGateways/Braintree/BraintreeApplePayPaymentEntry.vue'
import InvoicePaymentEntry from '../PaymentGateways/InvoicePaymentEntry.vue'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'
import BankPaymentEntry from '../PaymentGateways/BankPaymentEntry.vue'
import NotSupported from '../PaymentGateways/NotSupported.vue'

import { DeletePaymentFromCart } from '../../services/CartService'
import {
  AlertUnknownError,
  AlertErrorResponse,
  handleError,
} from '../../services/ErrorService.ts'
import {
  SetupBraintree,
  BrainTreeControlFolderName,
} from '../../services/PaymentGateways/BraintreeService.ts'
import {
  SetupBankPayment,
  BankPaymentControlFolderName,
} from '../../services/PaymentGateways/BankPaymentService.ts'
import { FormatCurrency } from '../../services/currencyFormatter.ts'

const props = defineProps<{
  carts: IAoeCart[] | null
  showdeletebutton: boolean
  showinvoice: boolean
  allowaddpayment: boolean
  excludedControlFolderNames?: string[]
}>()
const logger = useLogger()

logger.debug('AoePaymentControl', 'props', props)

const showInvoiceOption = ref<boolean>(props.showinvoice)
// Reactive (not just the value at mount) - this control stays mounted while a
// guest's cart is deactivated/reactivated in place (no tab switch), so a one-time
// ref would leave "add payment" enabled/disabled based on stale state.
const allowPaymentToBeAdded = computed<boolean>(() => props.allowaddpayment)
const columns = [
  {
    field: 'cardHolderName',
    title: t('partyOrderEntry.PaymentName'),
    cell: 'paymentCardHoldersName',
    width: resolveColumnWidth('180px', t('partyOrderEntry.PaymentName')),
  },
  {
    field: 'systemKeyword',
    title: t('partyOrderEntry.PaymentType'),
    cell: 'systemKeyword',
    width: resolveColumnWidth('120px', t('partyOrderEntry.PaymentType')),
  },
  {
    field: 'reference',
    title: t('partyOrderEntry.PaymentReference'),
    cell: 'paymentReference',
    width: resolveColumnWidth('160px', t('partyOrderEntry.PaymentReference')),
  },
  {
    field: 'transactionStateText',
    title: t('partyOrderEntry.PaymentStatus'),
    width: resolveColumnWidth('120px', t('partyOrderEntry.PaymentStatus')),
  },
  {
    field: 'charge',
    title: t('partyOrderEntry.PaymentCharge'),
    cell: 'paymentChargeTemplate',
    width: resolveColumnWidth('120px', t('partyOrderEntry.PaymentCharge')),
  },
  {
    field: 'deleteBtn',
    title: t('partyOrderEntry.Actions'),
    cell: 'paymentGridDelete',
    headerClassName: 'cart-item-grid-delete-header',
    width: resolveColumnWidth('60px', t('partyOrderEntry.Actions')),
    hidden: !props.showdeletebutton,
  },
]

export interface iCheckoutPaymentViewModel {
  name: string
  systemKeyword: string
  controlFolderName: string
  expanded: boolean
  parameters?: ICartPaymentMethodGetListItemParameters
}
const viewModels = ref<Array<iCheckoutPaymentViewModel>>([])
const currentCart = ref<IAoeCart>()
const BrainTreeViewModel = ref<iCheckoutPaymentViewModel | null>(null)

const BraintreeApplePayMissingParamsComment = defineComponent({
  name: 'BraintreeApplePayMissingParamsComment',
  props: {
    braintreeMerchantId: { type: [String, null] as PropType<string | null>, required: false, default: null },
    displayName: { type: [String, null] as PropType<string | null>, required: false, default: null },
  },
  setup(props) {
    let hasLogged = false
    return () => {
      const message = `BraintreeApplePayPaymentEntry not rendered: braintreeMerchantId=${String(
        props.braintreeMerchantId,
      )}, displayName=${String(props.displayName)}`

      if (!hasLogged) {
        hasLogged = true
        logger.debug('AoePaymentControl', 'BraintreeApplePayMissingParamsComment', message)
      }

      return createCommentVNode(message)
    }
  },
})

const expanded = computed(() => {
  const result: Array<boolean> = viewModels.value.map(panel => {
    return panel.expanded === true
  })
  return result
})

// Format currency based on cart's currency
function formatCurrency(value: number): string {
  if (value === undefined || value === null) return ''

  // Get currency from the current cart, fallback to first cart in props
  const cart =
    currentCart.value ||
    (props.carts && props.carts.length > 0 ? props.carts[0] : null)
  const currencyId = cart?.currencyId || null

  // Use FormatCurrency utility which handles culture and currency formatting
  const formatted = FormatCurrency(value, currencyId)
  return formatted || value.toString()
}

function expansionPanelHandler(index: number) {
  const currentValue = expanded.value[index]
  logger.debug('AoePaymentControl', 'expansionPanelHandler', index, currentValue)

  if (currentValue == true) {
    viewModels.value[index].expanded = false
  } else {
    CloseAllPaymentOptions()
    viewModels.value[index].expanded = true
  }
  logger.debug(
    'AoePaymentControl',
    'expansionPanelHandler',
    viewModels.value,
    expanded.value,
  )
}

const emit = defineEmits(['update-cart'])

onMounted(async () => {
  logger.debug('AoePaymentControl', 'onMounted', 'AoePaymentContol.vue')

  window.addEventListener('message', messagehandler)
})

// Watch for changes to cart balance and reinitialize payment gateways
watch(
  () => props.carts?.[0]?.balance,
  (newBalance, oldBalance) => {
    if (newBalance !== undefined && newBalance !== oldBalance) {
      logger.debug(
        'AoePaymentControl',
        'Cart balance changed:',
        newBalance,
        'Old balance:',
        oldBalance,
      )
      SetupPaymentGateways()
    }
  },
  { immediate: false },
)

async function deletePaymentClickedHandler(
  event: MouseEvent,
  lineNumber: number,
) {
  logger.debug('AoePaymentControl', 'deletePaymentClickedHandler', lineNumber)
  try {
    await DeletePaymentFromCart({
      aoeCartIdToken: props.carts != null ? props.carts[0].aoeCartIdToken : '',
      lineNumber,
    })
    emit('update-cart', null)
  } catch (error: unknown) {
    logger.error('AoePaymentControl', 'Error deleting payment:', error)
    await handleError(error, t)
  }
}

async function messagehandler(e: AoeMessageEvent) {
  const allowedOrigin = `${window.location.protocol}//${window.location.hostname}`
  if (!e.origin.startsWith(allowedOrigin)) {
    logger.debug(
      'AoePaymentControl',
      'messagehandler',
      'origin not allowed',
      e.origin,
      allowedOrigin,
    )
    return
  }
  if (e.data && e.data.messageType === 'aoepaymentcreated') {
    logger.debug(
      'AoePaymentControl',
      'messagehandler',
      'aoepaymentcreated',
      e.data.messageData,
    )
    CloseAllPaymentOptions()
    emit('update-cart', null)
  }
  if (e.data && e.data.messageType === 'aoepaymentcreationerror') {
    logger.debug(
      'AoePaymentControl',
      'messagehandler',
      'aoepaymentcreationerror',
      e.data.messageData,
    )
    CloseAllPaymentOptions()

    const result = await AlertErrorResponse(
      t,
      e.data.messageData as IErrorResponse,
    )
    if (result) {
      return
    }
    AlertUnknownError(t)
  }
}

function CloseAllPaymentOptions() {
  viewModels.value.forEach(element => {
    element.expanded = false
  })
}

function GetAvailablePaymentMethods(): Array<iCheckoutPaymentViewModel> {
  logger.debug('AoePaymentControl', 'GetAvailablePaymentMethods', props.carts)

  if (
    props.carts != null &&
    props.carts.length > 0 &&
    props.carts[0].balance > 0 &&
    viewModels.value.length === 0
  ) {
    currentCart.value = props.carts[0]

    let paymentMethods = props.carts[0].availablePaymentMethods

    if (!showInvoiceOption.value) {
      paymentMethods = props.carts[0].availablePaymentMethods.filter(
        paymentMethod => paymentMethod.controlFolderName   !== 'InvoicePayment',
      )
    }

    if (props.excludedControlFolderNames && props.excludedControlFolderNames.length > 0) {
      const excluded = props.excludedControlFolderNames
      paymentMethods = paymentMethods.filter(
        paymentMethod => !excluded.includes(paymentMethod.controlFolderName)
      )
    }

    viewModels.value = paymentMethods.map(paymentMethod => {
      return {
        name: paymentMethod.name,
        systemKeyword: paymentMethod.systemKeyword,
        controlFolderName: paymentMethod.controlFolderName,
        expanded: false,
        parameters: paymentMethod.parameters,
      }
    })

    BrainTreeViewModel.value =
      viewModels.value.find(
        vm => vm.controlFolderName === BrainTreeControlFolderName,
      ) ?? null

    logger.debug(
      'AoePaymentControl',
      'BrainTreeViewModel',
      BrainTreeViewModel.value?.systemKeyword ?? null,
      BrainTreeViewModel.value?.parameters?.BraintreeMerchantId ?? null,
      BrainTreeViewModel.value?.parameters?.DisplayName ?? null,
    )

    logger.debug('AoePaymentControl', 'Payment view models:', viewModels.value)

    SetupPaymentGateways()
  }

  return viewModels.value
}

async function SetupPaymentGateways() {
  logger.debug('AoePaymentControl', 'SetupPaymentGateways called')
  if (
    props.carts != null &&
    props.carts.length > 0 &&
    props.carts[0].balance > 0
  ) {
    logger.debug('AoePaymentControl', 'SetupPaymentGateways', 'pre setup')
    await SetupBraintree(t, props.carts[0], viewModels.value)
    await SetupBankPayment(t, props.carts[0], viewModels.value)
    logger.debug('AoePaymentControl', 'SetupPaymentGateways', 'post setup')
  }
}

function GetDataItemsForGrid() {
  let payments: Array<IAoeCartPayment> = []
  if (props.carts != null) {
    for (const cart of props.carts) {
      if (cart.isActive && cart.payments != null) {
        payments = payments.concat(cart.payments)
      }
    }
  }
  return payments
}

function updateCartHandler(event: MouseEvent) {
  logger.debug('AoePaymentControl', 'updateCartHandler', event)
  emit('update-cart', event)
}

function translatePaymentMethod(systemKeyword: string, paymentType: string): string {
   let translationKey = `checkout.paymentMethods.${systemKeyword}`
  if(paymentType){
    if(paymentType.toLowerCase() === 'paypalaccount'){
        translationKey = `checkout.paymentMethods.PayPal`
    }
  }
  if(paymentType){
    if(paymentType.toLowerCase() === 'eps'){
        translationKey = `checkout.paymentMethods.EPS`
    }
  }

  const translated = t(translationKey)
  // If translation key doesn't exist, t() returns the key itself
  return translated !== translationKey ? translated : systemKeyword
}

</script>
<template>
  <div
    v-if="props.carts != null && props.carts.length > 0"
    class="aoe-payment-controls"
  >
    <div class="row mt-3 party-order-entry-section-header-row">
      <div
        class="col-12 party-order-entry-section-header-div payment-control-section-header"
      >
        {{ t('partyOrderEntry.PaymentSectionHeader') }}
      </div>
    </div>
    <div
      v-if="GetDataItemsForGrid().length > 0"
      class="row mt-3 aoe-payment-grid"
    >
      <div class="col-12 payment-item-grid">
        <Grid
          ref="gridPaymentItems"
          :data-items="GetDataItemsForGrid()"
          :resizable="true"
          :pageable="false"
          class="payment-item-grid"
          :columns="columns"
        >
          <template v-slot:paymentCardHoldersName="{ props }">
            <td
              v-if="props.rowType !== 'groupHeader'"
              class="payment-grid-reference"
            >
              <template v-if="props.dataItem.systemKeyword.startsWith('TTGBraintree')">
                <!-- Credit card payment -->
                <template v-if="props.dataItem.attributes['cardholderName']">
                  {{ props.dataItem.attributes['cardholderName'] }}
                </template>
                <!-- PayPal payment -->
                <template
                  v-else-if="
                    props.dataItem.attributes['firstName'] &&
                    props.dataItem.attributes['lastName']
                  "
                >
                  {{ props.dataItem.attributes['firstName'] }}
                  {{ props.dataItem.attributes['lastName'] }}
                </template>
              </template>
              <template v-else>
                <template v-if="props.dataItem.attributes['accountHolder']">
                  {{ props.dataItem.attributes['accountHolder'] }}
                </template>
              </template>
            </td>
          </template>
          <template v-slot:systemKeyword="{ props }">
            <td
              v-if="props.rowType !== 'groupHeader'"
              class="payment-grid-reference"
            >
              {{ translatePaymentMethod(props.dataItem.systemKeyword, props.dataItem.attributes.type) }}
            </td>
          </template>
          <template v-slot:paymentReference="{ props }">
            <td
              v-if="props.rowType !== 'groupHeader'"
              class="payment-grid-reference"
            >
              <template v-if="props.dataItem.systemKeyword.startsWith('TTGBraintree')">
                <!-- Credit card payment -->
                <template
                  v-if="
                    props.dataItem.attributes['cardType'] &&
                    props.dataItem.attributes['lastFour']
                  "
                >
                  {{
                    props.dataItem.attributes['cardType'] +
                    ' - ' +
                    '***' +
                    props.dataItem.attributes['lastFour'] +
                    ' - ' +
                    props.dataItem.attributes['expirationMonth'] +
                    '/' +
                    props.dataItem.attributes['expirationYear']
                  }}
                </template>
                <!-- PayPal payment -->
                <template v-else-if="props.dataItem.attributes['email']">
                  PayPal - {{ props.dataItem.attributes['email'] }}
                </template>
              </template>
            </td>
          </template>
          <template v-slot:paymentChargeTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="text-end">
              {{ formatCurrency(props.dataItem.charge) }}
            </td>
          </template>
          <template v-slot:paymentGridDelete="{ props }">
            <td
              v-if="props.rowType !== 'groupHeader'"
              class="grid-delete payment-grid-delete text-center"
            >
              <KButton
                :fill-mode="'clear'"
                @click="
                  deletePaymentClickedHandler($event, props.dataItem.lineNumber)
                "
                :svg-icon="trashIcon"
                :theme-color="'error'"
                class="payment-grid-delete-icon"
              ></KButton>
            </td>
          </template>
        </Grid>
      </div>
    </div>
    <div
      v-if="
        allowPaymentToBeAdded &&
        props.carts[0].balance &&
        props.carts[0].balance > 0
      "
      class="row mt-3"
    >
      <div class="col-12 payment-section-title">
        {{ t('partyOrderEntry.MakePaymentTitle') }}:
      </div>
    </div>
    <div
      v-if="
        allowPaymentToBeAdded &&
        props.carts[0].balance &&
        props.carts[0].balance > 0
      "
      class="row mt-3"
    >
      <div class="col-12 payment-item-grid">
        <div id="braintreepaypalbutton"></div>
        <div id="braintreepaypalbutton-placeholder" class="paypal-button-placeholder">
          <KLoader type="infinite-spinner" size="small" />
        </div>
        <div class="apple-pay-payment-control-wrapper">
          <BraintreeApplePayPaymentEntry
            v-if="
              BrainTreeViewModel &&
              BrainTreeViewModel.parameters?.BraintreeMerchantId &&
              BrainTreeViewModel.parameters?.DisplayName
            "
            :cart="props.carts[0]"
            :systemKeyword="BrainTreeViewModel.systemKeyword"
            :parameters="{
              braintreeMerchantId: BrainTreeViewModel.parameters!.BraintreeMerchantId!,
              displayName: BrainTreeViewModel.parameters!.DisplayName!,
            }"
          />
          <template v-else>
            <BraintreeApplePayMissingParamsComment
              :braintreeMerchantId="BrainTreeViewModel?.parameters?.BraintreeMerchantId ?? null"
              :displayName="BrainTreeViewModel?.parameters?.DisplayName ?? null"
            />
          </template>
        </div>
        <KExpansionPanel
          v-for="(
            viewModel, index
          ) in GetAvailablePaymentMethods() as Array<iCheckoutPaymentViewModel>"
          :title="viewModel.name"
          :title-render="() => h('button', { class: 'panel-header-button k-button k-button-md k-button-solid k-button-solid-primary' }, viewModel.name)"
          :expanded="expanded[index]"
          :tab-index="index"
          :key="index"
          :selected="viewModel.expanded"
          @action="expansionPanelHandler(index)"
        >
          <KReveal
            v-if="viewModel.controlFolderName !== BankPaymentControlFolderName"
            :appear="expanded[index]"
          >
            <KExpansionPanelContent v-if="expanded[index]">
              <BrainTree
                v-if="
                  viewModel.controlFolderName === BrainTreeControlFolderName
                "
                :cart="props.carts[0]"
                :systemKeyword="viewModel.systemKeyword"
                :name="viewModel.name"
                :check3DS="viewModel.parameters?.Check3DS === true || viewModel.parameters?.Check3DS === 'true'"
                :submitbuttonmessage="'partyOrderEntry.CreditCartSubmit'"
              ></BrainTree>
              <InvoicePaymentEntry
                v-else-if="viewModel.controlFolderName === 'InvoicePayment'"
                :cart="props.carts[0]"
                @update-cart="updateCartHandler($event)"
              ></InvoicePaymentEntry>
              <NotSupported
                v-else
                :system-keyword="viewModel.systemKeyword"
                :name="viewModel.name"
              ></NotSupported>
            </KExpansionPanelContent>
          </KReveal>
          <KExpansionPanelContent v-else-if="expanded[index]">
            <BankPaymentEntry
              :cart="props.carts[0]"
              :systemKeyword="viewModel.systemKeyword"
              :name="viewModel.name"
              @update-cart="updateCartHandler($event)"
            ></BankPaymentEntry>
          </KExpansionPanelContent>
        </KExpansionPanel>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.aoe-payment-controls {
  #braintreepaypalbutton {
    padding-block: var(--kendo-spacing-3, .75rem);
    padding-inline: var(--kendo-spacing-4, 1rem);
  }

  .apple-pay-payment-control-wrapper {
    padding-top: $spacing-3;
    padding-right: $spacing-4;
    padding-bottom: $spacing-3;
    padding-left: $spacing-4;
  }

  .panel-header-button {
    border: none;
    width: 100%;
    text-transform: uppercase;
    @include typography-body-large;
  }
}
</style>
